import { useState } from "react";
import { Box } from "@mui/material";
import UploadFile from "@mui/icons-material/UploadFile";
import UploadModal, { ErrorableFileState } from "components/UploadModal/UploadModal";
import UploadView from "components/UploadView/UploadView";
import {
	AttachedFilesContainer,
	AttachedFilesContainerTitle,
	PhotoOrSketchCustomizationContainer,
	PhotoOrSketchCustomizationLegendContainer,
	PhotoOrSketchCustomizationLegendText,
	PhotoOrSketchCustomizationLegendTextBold,
	PhotoOrSketchText,
	StyledUploadButton
} from "./SketchUpload.styles";
import {
	AttachedFiles,
	AttachFile,
	DefaultSupportedFiles,
	MaxFileSize,
	MaxFileSizeHeader,
	PerImage,
	PhotoOrSketchCustomizationText,
	PleaseAttachImagesText,
	SupportedFilesHeader
} from "constants/text";

interface SketchUploadProps {
	maxFiles?: number;
	disabled?: boolean;
	variant?: "contained" | "outlined" | "text";
	onFilesSelected?: (files: File[]) => void;
}

export const SketchUpload = ({
	maxFiles = 3,
	disabled = false,
	variant = "outlined",
	onFilesSelected
}: SketchUploadProps) => {
	const [open, setOpen] = useState(false);
	const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

	const dummyItem = { id: "custom-quote-upload" } as any;

	const handleClose = (files: ErrorableFileState[]) => {
		const validFiles = files.map(({ file }) => file);
		setUploadedFiles((prev) => [...prev, ...validFiles]);
		onFilesSelected?.(validFiles);
		setOpen(false);
	};

	const removeFile = (index: number) => {
		const newFiles = uploadedFiles.filter((_, i) => i !== index);
		setUploadedFiles(newFiles);
		onFilesSelected?.(newFiles);
	};

	return (
		<PhotoOrSketchCustomizationContainer>
			<PhotoOrSketchText>{PhotoOrSketchCustomizationText}</PhotoOrSketchText>

			{uploadedFiles.length < maxFiles && (
				<Box>
					<StyledUploadButton
						variant={variant}
						onClick={() => setOpen(true)}
						disabled={disabled || uploadedFiles.length >= maxFiles}
						startIcon={<UploadFile />}
					>
						{AttachFile}
					</StyledUploadButton>
				</Box>
			)}

			<PhotoOrSketchCustomizationLegendContainer>
				<PhotoOrSketchCustomizationLegendText>
					{SupportedFilesHeader}&nbsp;
					<PhotoOrSketchCustomizationLegendTextBold>
						{DefaultSupportedFiles}
					</PhotoOrSketchCustomizationLegendTextBold>
				</PhotoOrSketchCustomizationLegendText>
				<PhotoOrSketchCustomizationLegendText>
					{MaxFileSizeHeader}&nbsp;
					<PhotoOrSketchCustomizationLegendTextBold>{MaxFileSize}</PhotoOrSketchCustomizationLegendTextBold>
					&nbsp;{PerImage}
				</PhotoOrSketchCustomizationLegendText>
			</PhotoOrSketchCustomizationLegendContainer>

			{uploadedFiles.length > 0 && (
				<AttachedFilesContainer>
					<AttachedFilesContainerTitle>{AttachedFiles}</AttachedFilesContainerTitle>
					{uploadedFiles.map((file, index) => (
						<UploadView
							key={`${file.name}-${index}`}
							index={index}
							file={{
								name: file.name,
								size: file.size,
								fileUrl: URL.createObjectURL(file),
								id: `file-${index}`,
								originalLineItemId: ""
							}}
							onImageRemove={() => removeFile(index)}
						/>
					))}
				</AttachedFilesContainer>
			)}

			<UploadModal
				handleClose={handleClose}
				item={dummyItem}
				open={open}
				maxFiles={maxFiles - uploadedFiles.length}
				fileUploads={[]}
				fileSelectionText={PleaseAttachImagesText}
			/>
		</PhotoOrSketchCustomizationContainer>
	);
};

export default SketchUpload;
